import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../utils/apiConfig';

const POLL_INTERVAL = 60_000;
const TIMEFRAMES = ['1h', '4h', 'daily'];

export function useDashboardStats({ hours = 24, topCoinsLimit = 10 } = {}) {
  const [tradeSizeDistribution, setTradeSizeDistribution] = useState(null);
  const [btcPredictions, setBtcPredictions] = useState(null);
  const [btcTimeframe, setBtcTimeframe] = useState('1h');
  const [topCoinsByCount, setTopCoinsByCount] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const btcTimeframeRef = useRef(btcTimeframe);

  useEffect(() => {
    btcTimeframeRef.current = btcTimeframe;
  }, [btcTimeframe]);

  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/dashboard-stats?hours=${hours}&topCoinsLimit=${topCoinsLimit}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch dashboard stats: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch dashboard stats');
      }

      if (result.tradeSizeDistribution) {
        setTradeSizeDistribution(result.tradeSizeDistribution);
      }

      if (result.btcPredictions) {
        setBtcPredictions(result.btcPredictions);
        // Auto-switch if current timeframe is null
        if (result.btcPredictions[btcTimeframeRef.current] == null) {
          const available = TIMEFRAMES.find((tf) => result.btcPredictions[tf] != null);
          if (available) setBtcTimeframe(available);
        }
      }

      if (result.topCoinsByCount) {
        setTopCoinsByCount(result.topCoinsByCount);
      }

      setError(null);
    } catch (err) {
      console.error('[DashboardStats] Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [hours, topCoinsLimit]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Helper to format range display (same as useTradeSizeDistribution)
  const formatRange = (range) => {
    const labels = {
      '10K-100K': '$10K - $100K',
      '100K-500K': '$100K - $500K',
      '500K-1M': '$500K - $1M',
      '1M-10M': '$1M - $10M',
      '10M+': '$10M+',
    };
    return labels[range] || range;
  };

  // Compute totalTrades from distribution
  const totalTrades = tradeSizeDistribution
    ? Object.values(tradeSizeDistribution).reduce(
        (sum, r) => sum + (typeof r === 'object' && r !== null ? r.count || 0 : 0),
        0
      )
    : 0;

  return {
    // Trade size distribution
    tradeSizeDistribution,
    totalTrades,
    formatRange,
    // BTC predictions
    btcPredictions,
    btcTimeframe,
    setBtcTimeframe,
    // Top coins
    topCoinsByCount,
    // General
    loading,
    error,
    refresh: fetchData,
  };
}

export default useDashboardStats;
